import React, { useState } from "react";
import "./WomenConnectSignup.css";
import heroImage from "../../images/hero-image.jpg";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import ProfileService from "../../api/ProfileService"; 


// ------------------ Step 1: Personal Info ------------------
const PersonalStep = ({ data, onChange, onNext, loading }) => {
  const valid = data.fullName && data.email && data.phone;
  return (
    <form className="step-form" onSubmit={(e) => e.preventDefault()}>
      <div className="form-group">
        <label>Full Name</label>
        <input name="fullName" value={data.fullName} onChange={onChange} />
      </div>

      <div className="form-group">
        <label>Email</label>
        <input name="email" type="email" value={data.email} onChange={onChange} />
      </div>

      <div className="form-group">
        <label>Phone Number</label>
        <input name="phone" value={data.phone} onChange={onChange} />
      </div>

      <div className="actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onNext}
          disabled={!valid || loading}
        >
          {loading ? "Processing..." : "Continue →"}
        </button>
      </div>
    </form>
  );
};

// ------------------ Step 2: Business Info ------------------
const BusinessStep = ({ data, onChange, onNext, onBack, categories = [], loading }) => {
  const valid = data.category && data.businessDetails && data.serviceName && data.location;

  return (
    <form className="step-form" onSubmit={(e) => e.preventDefault()}>
      <div className="row">
        <div className="col">
          <div className="form-group">
            <label>Service Name</label>
            <input
              name="serviceName"
              value={data.serviceName}
              onChange={onChange}
              placeholder="e.g., Consultant, Hair Stylist"
            />
          </div>
        </div>

        <div className="col">
          <div className="form-group">
            <label>Location</label>
            <input
              name="location"
              value={data.location}
              onChange={onChange}
              placeholder="City, State or full address"
            />
          </div>
        </div>

        <div className="col">
          <div className="form-group">
            <label>Business Category</label>
            <select name="category" value={data.category} onChange={onChange}>
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.subCategoryId} value={c.subCategoryId}>
                  {c.subCategoryName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-group">
        <label>Service Description</label>
        <textarea
          name="businessDetails"
          value={data.businessDetails}
          onChange={onChange}
          rows={4}
          placeholder="Describe your services"
        />
      </div>

      <div className="row">
        <div className="col">
          <div className="form-group">
            <label>Service Logo</label>
            <input
              type="file"
              name="logo"
              onChange={(e) =>
                onChange({ target: { name: "logo", value: e.target.files[0] } })
              }
            />
            {data.logo && <div className="file-name">{data.logo.name}</div>}
          </div>
        </div>

        <div className="col">
          <div className="form-group">
            <label>Gallery Images</label>
            <input
              type="file"
              name="images"
              multiple
              onChange={(e) =>
                onChange({
                  target: { name: "images", value: Array.from(e.target.files) },
                })
              }
            />
            {data.images && data.images.length > 0 && (
              <div className="file-name">{data.images.length} image(s)</div>
            )}
          </div>
        </div>
      </div>

      <div className="actions">
        <button type="button" className="btn btn-outline" onClick={onBack}>
          ← Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onNext}
          disabled={!valid || loading}
        >
          {loading ? "Checking User..." : "Continue →"}
        </button>
      </div>
    </form>
  );
};

// ------------------ Step 3: OTP Verification ------------------
const VerifyStep = ({ phone, otp, setOtp, onBack, onVerify, loading }) => {
  return (
    <div className="step-form text-center">
      <div className="icon-circle">📱</div>
      <h3>Verify Your Phone</h3>
      <p className="muted">We’ve sent a 6-digit OTP to</p>
      <p className="font-bold">{phone}</p>

      <div className="otp-input">
        <input
          type="text"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          maxLength={6}
          placeholder="000000"
        />
      </div>

      <div className="actions">
        <button className="btn btn-outline" onClick={onBack}>
          ← Back
        </button>
        <button
          className="btn btn-primary"
          onClick={onVerify}
          disabled={otp.length !== 6 || loading}
        >
          {loading ? "Verifying..." : "Verify & Complete"}
        </button>
      </div>
    </div>
  );
};

// ------------------ Step 4: Done ------------------
const DoneStep = ({ onExplore, onHome }) => (
  <div className="step-form text-center done">
    <div className="done-icon">✔️</div>
    <h2>You're All Set!</h2>
    <p className="muted">
      Your business directory is now live and customers can discover your services.
    </p>
    <div className="actions">
      <button className="btn btn-primary" onClick={onExplore}>
        Explore Services
      </button>
      <button className="btn btn-outline" onClick={onHome}>
        Go to Homepage
      </button>
    </div>
  </div>
);

// ------------------ Parent Component ------------------
export default function WomenConnectSignup({ onBack, bussinessCategories = [] }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpId, setOtpId] = useState(null);

  const [personalData, setPersonalData] = useState({
    fullName: "",
    email: "",
    phone: "",
  });

  const [businessData, setBusinessData] = useState({
    category: "",
    businessDetails: "",
    serviceName: "",
    location: "",
    logo: null,
    images: [],
  });

  // ---------- Handlers ----------
  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setPersonalData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBusinessChange = (e) => {
    const { name, value } = e.target;
    setBusinessData((prev) => ({ ...prev, [name]: value }));
  };

  // ---------- OTP Sending ----------
  const sendOtp = async () => {
    setLoading(true);
    const payload = {
      type: "phone",
      phoneNumber: personalData.phone,
      optModelName: "Bussiness Directory Signup",
    };
    try {
      const response = await ProfileService.sendOtp(payload);
      if (response.data?.data?.id) {
        setOtpId(response.data.data.id);
        toast.success("OTP sent successfully!");
        setStep(3);
      } else {
        toast.error("Failed to send OTP.");
      }
    } catch (e) {
      console.error("sendOtp error:", e);
      toast.error("Something went wrong while sending OTP.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Verify OTP ----------
const handleVerify = async () => {
  if (otp.length !== 6) {
    toast.warning("Enter a valid 6-digit OTP.");
    return;
  }

  setLoading(true);
  try {
    const payload = { id: otpId, otp }; // ✅ backend expects 'id'

    const response = await ProfileService.validateUserOtp(payload);
    const res = response?.data;

    if (res?.status === 1 && res?.data === "VALID_OTP") {
      toast.success("Phone verified successfully!");
      
      // Build FormData to save the business directory
      const formData = new FormData();
      
      // Personal info
      formData.append("firstName", personalData.fullName);
      formData.append("email", personalData.email);
      formData.append("phoneNumber", personalData.phone);

      // Business info
      formData.append("serviceName", businessData.serviceName);
      formData.append("location", businessData.location);
      formData.append("serviceCategory", businessData.category);
      formData.append("serviceDetails", businessData.businessDetails);

      // Logo (single file)
      if (businessData.logo) {
        formData.append("serviceImage", businessData.logo);
      }

      // Gallery images (multiple files)
      if (businessData.images && businessData.images.length > 0) {
        businessData.images.forEach((file) => {
          formData.append("serviceGalleryImage", file);
        });
      }

      // Flag to tell backend it's a web page submission
      formData.append("isBusinessDirWebPage", true);

      // Call your API
      const saveRes = await ProfileService.directoryAndServiceRegister(formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (saveRes.data?.status === 1) {
        toast.success("Business directory saved successfully!");
        setStep(4); // Move to Done step
      } else {
        toast.error(saveRes.data?.message || "Failed to save directory.");
      }

    } else if (res?.data === "TIME_EXPIRED") {
      toast.warning("OTP has expired. Please request a new one.");
    } else if (res?.data === "IN_VALID_OTP") {
      toast.error("Invalid OTP. Please try again.");
    } else {
      toast.error("Something went wrong. Please try again.");
    }
  } catch (e) {
    console.error("verifyOtp error:", e);
    toast.error("Error verifying OTP or saving directory.");
  } finally {
    setLoading(false);
  }
};


  // ---------- Step Navigation ----------
  const goNext = async () => {
    if (step === 1) {
      // Proceed to business info
      setStep(2);
      return;
    }

    if (step === 2) {
      // Validate and check user
      if (
        !personalData.fullName ||
        !personalData.email ||
        !personalData.phone ||
        !businessData.category ||
        !businessData.businessDetails ||
        !businessData.serviceName ||
        !businessData.location
      ) {
        toast.warning("Please fill all required fields.");
        return;
      }

      setLoading(true);
      try {
        // Step 1: Check user exists
        const payload = {
          email: personalData.email,
          phoneNumber: personalData.phone,
        };
        const res = await ProfileService.checkUserExists(payload);
        const { status } = res.data;

        if (status === 2) {
          toast.error("Email already exists. Please use another email.");
        } else if (status === 3) {
          toast.error("Phone number already registered.");
        } else if (status === 1) {
          toast.success("User not found. Sending OTP...");
          await sendOtp();
        } else {
          toast.error("Unexpected response. Try again.");
        }
      } catch (error) {
        console.error("checkUserExists error:", error);
        toast.error("Network error. Try again later.");
      } finally {
        setLoading(false);
      }
    }
  };

  const goBack = () => setStep((s) => Math.max(1, s - 1));

  return (
    <div className="signup-layout">
      <div className="left-panel">
        <div className="container">
          {/* Progress Bar */}
          <div className="progress-wrap">
            {[1, 2, 3, 4].map((p) => (
              <div key={p} className={`step-item ${step >= p ? "active" : ""}`}>
                <div className="circle">{step > p ? "✔" : p}</div>
                {p < 4 && <div className={`line ${step > p ? "filled" : ""}`} />}
              </div>
            ))}
          </div>

          <div className="card">
            {step === 1 && (
              <>
                <h1>Let's Get Started</h1>
                <p className="muted">Share your personal details</p>
                <PersonalStep
                  data={personalData}
                  onChange={handlePersonalChange}
                  onNext={goNext}
                  loading={loading}
                />
              </>
            )}

            {step === 2 && (
              <>
                <h1>Tell Us About Your Business</h1>
                <BusinessStep
                  data={businessData}
                  onChange={handleBusinessChange}
                  onNext={goNext}
                  onBack={goBack}
                  categories={bussinessCategories}
                  loading={loading}
                />
              </>
            )}

            {step === 3 && (
              <VerifyStep
                phone={personalData.phone}
                otp={otp}
                setOtp={setOtp}
                onBack={goBack}
                onVerify={handleVerify}
                loading={loading}
              />
            )}

            {step === 4 && (
              <DoneStep
                onExplore={() => alert("Explore Services")}
                onHome={() => (window.location.href = "/")}
              />
            )}
          </div>

          <button
            className="wc-back-link wc-main-back-btn"
            onClick={onBack}
            style={{
              position: "absolute",
              left: "20px",
              top: "20px",
              background: "none",
              border: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              color: "#9ca3af",
              fontSize: "0.9rem",
              fontWeight: "500",
              cursor: "pointer",
              padding: "8px 16px",
              borderRadius: "6px",
              transition: "color 0.2s ease",
              zIndex: 10,
            }}
          >
            <FaArrowLeft /> Back to Home
          </button>
        </div>
      </div>

      <aside className="right-panel">
        <div className="overlay" />
        <img src={heroImage} alt="hero" className="hero-img" />
        <div className="hero-content">
          <h2>Join a Thriving Community of Women Leaders</h2>
          <p className="muted">
            Take your business to new heights with support from thousands of successful women entrepreneurs.
          </p>
        </div>
      </aside>
    </div>
  );
}
